import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { 
  Car, Plus, ChevronLeft, Loader2, Trash2, Edit, 
  Fuel, Navigation, Calendar, Euro, CheckCircle, AlertCircle
} from 'lucide-react';

const VehiclesPage = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    plate_number: '',
    brand: '',
    model: '',
    year: '',
    fuel_type: 'gasoline',
    km_rate: '0.50'
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchVehicles = useCallback(async () => {
    const token = localStorage.getItem('optitrajet_token');
    if (!token) {
      navigate('/optitrajet/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/optitrajet/fleet/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVehicles(data.vehicles || []);
      }
    } catch (err) {
      console.error('Fetch vehicles error:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, navigate]);

  useEffect(() => {
    setMounted(true);
    fetchVehicles();
  }, [fetchVehicles]);

  const resetForm = () => {
    setFormData({
      name: '',
      plate_number: '',
      brand: '',
      model: '',
      year: '',
      fuel_type: 'gasoline',
      km_rate: '0.50'
    });
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (vehicle) => {
    setFormData({
      name: vehicle.name || '',
      plate_number: vehicle.plate_number || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      fuel_type: vehicle.fuel_type || 'gasoline',
      km_rate: vehicle.km_rate?.toString() || '0.50'
    });
    setEditingId(vehicle.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const token = localStorage.getItem('optitrajet_token');
    const url = editingId 
      ? `${API_URL}/api/optitrajet/fleet/vehicles/${editingId}`
      : `${API_URL}/api/optitrajet/fleet/vehicles`;

    try {
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          year: formData.year ? parseInt(formData.year) : null,
          km_rate: parseFloat(formData.km_rate) || 0.50
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erreur');
      }

      await fetchVehicles();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm('Supprimer ce véhicule ?')) return;
    
    const token = localStorage.getItem('optitrajet_token');
    try {
      await fetch(`${API_URL}/api/optitrajet/fleet/vehicles/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setVehicles(vehicles.filter(v => v.id !== id));
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const fuelTypes = {
    gasoline: { name: 'Essence', icon: '⛽' },
    diesel: { name: 'Diesel', icon: '🛢️' },
    electric: { name: 'Électrique', icon: '⚡' },
    hybrid: { name: 'Hybride', icon: '🔋' }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8FAFC' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 lg:pb-8" style={{ background: '#F8FAFC', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/optitrajet/dashboard')}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-slate-600" />
            </button>
            <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800">
              Mes véhicules
            </h1>
          </div>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 rounded-xl h-10"
          >
            <Plus className="h-4 w-4 mr-1" />
            Ajouter
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={resetForm}>
            <Card 
              className="w-full max-w-md border-0 shadow-2xl animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <CardContent className="p-6">
                <h2 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800 mb-4">
                  {editingId ? 'Modifier le véhicule' : 'Nouveau véhicule'}
                </h2>

                {error && (
                  <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Nom</label>
                      <Input
                        placeholder="Ma voiture"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="h-11 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Plaque</label>
                      <Input
                        placeholder="AB-123-CD"
                        value={formData.plate_number}
                        onChange={(e) => setFormData({ ...formData, plate_number: e.target.value })}
                        className="h-11 rounded-xl"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Marque</label>
                      <Input
                        placeholder="Renault"
                        value={formData.brand}
                        onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                        className="h-11 rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Modèle</label>
                      <Input
                        placeholder="Clio"
                        value={formData.model}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        className="h-11 rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Année</label>
                      <Input
                        type="number"
                        placeholder="2022"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                        className="h-11 rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Carburant</label>
                      <select
                        value={formData.fuel_type}
                        onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                        className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white"
                      >
                        {Object.entries(fuelTypes).map(([key, val]) => (
                          <option key={key} value={key}>{val.icon} {val.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Taux kilométrique (€/km)
                    </label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.50"
                        value={formData.km_rate}
                        onChange={(e) => setFormData({ ...formData, km_rate: e.target.value })}
                        className="pl-10 h-11 rounded-xl"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Utilisé pour calculer automatiquement le montant des trajets</p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={resetForm} className="flex-1 h-11 rounded-xl">
                      Annuler
                    </Button>
                    <Button type="submit" disabled={saving} className="flex-1 h-11 rounded-xl bg-blue-600 hover:bg-blue-700">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? 'Modifier' : 'Ajouter'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Vehicles List */}
        <div className={`space-y-3 transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {vehicles.length > 0 ? (
            vehicles.map((vehicle, idx) => {
              const fuel = fuelTypes[vehicle.fuel_type] || fuelTypes.gasoline;
              return (
                <Card 
                  key={vehicle.id}
                  className="border border-slate-100 shadow-sm hover:shadow-md transition-all"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Car className="h-7 w-7 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-slate-800">{vehicle.name}</p>
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                            {vehicle.plate_number}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500">
                          {vehicle.brand} {vehicle.model} {vehicle.year && `(${vehicle.year})`}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            {fuel.icon} {fuel.name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Euro className="h-3 w-3" />
                            {vehicle.km_rate}€/km
                          </span>
                          <span className="flex items-center gap-1">
                            <Navigation className="h-3 w-3" />
                            {vehicle.total_km?.toLocaleString() || 0} km
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEdit(vehicle)}
                          className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteVehicle(vehicle.id)}
                          className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="border border-dashed border-slate-200 bg-slate-50/50">
              <CardContent className="p-8 text-center">
                <Car className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">Aucun véhicule enregistré</p>
                <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un véhicule
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default VehiclesPage;
